import { rem } from 'polished'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import { MarginContainer } from './common'
import SortSavedItemsModal from './SortSavedItemsModal'
import ItemCard from '../ItemCard/ItemCard'
import SavedOfferTripLabel from '../SavedOffers/SavedOfferTripLabel'

import {
  tripDrawerSavedItemsListFilterOptionSelected,
  tripDrawerSavedItemsListSortOptionSelected,
  tripDrawerSavedItemsListTripClicked,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import DropdownList from 'components/Luxkit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'components/Luxkit/Icons/line/LineAngleDownIcon'
import ListItem from 'components/Luxkit/List/ListItem'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useToggle from 'hooks/useToggle'
import { arrayToObject, sortBy } from 'lib/array/arrayUtils'
import { capitalise } from 'lib/string/stringUtils'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import { newDetailCardImplementedTypes } from 'tripPlanner/components/DetailItemCard/constants'
import DetailItemCard from 'tripPlanner/components/DetailItemCard/DetailItemCard'
import { SAVED_ITEMS_LIST_LAST_SELECTED_SORT_OPTION_STORAGE_KEY } from 'tripPlanner/config'
import TripContext, { TripContextValue } from 'tripPlanner/contexts/TripContext'
import { BasicTrip } from 'tripPlanner/types/common'
import { TripItem } from 'tripPlanner/types/tripItem'
import { getTripPermissions } from 'tripPlanner/utils'
import { filterProducts, filterStatus } from 'tripPlanner/utils/summary'

const OldItemCardContainer = styled(MarginContainer)`
  background-color: ${(props) => props.theme.palette.neutral.default.eight};
  padding-block: ${rem(16)};
`

const ItemsContainer = styled(Group)`
  padding-top: ${rem(8)};
  background-color: ${(props) => props.theme.palette.neutral.default.six};
`

export const TRIP_ITEMS_SORT_OPTIONS = [
  'recently added',
  'alphabetical',
  'by trip',
] as const

export type TripItemsSortOptions = (typeof TRIP_ITEMS_SORT_OPTIONS)[number]

interface Props {
  trips: Array<BasicTrip>
  tripItems: Array<TripItem>
  filters: Array<App.TripPlannerSubFilterType>
}

function SavedItemsList({ trips, filters, tripItems }: Props) {
  const [sortOption, setSortOption] =
    useState<TripItemsSortOptions>('recently added')
  const sortChipRef = useRef(null)
  const { value: isDropdownOpen, toggle: toggleDropdown } = useToggle()

  const tripMap = useMemo(() => {
    return arrayToObject<
      BasicTrip,
      { trip: BasicTrip; context: TripContextValue }
    >(
      trips,
      (trip) => trip.id,
      (trip) => ({
        trip,
        context: {
          tripId: trip.id,
          viewAsTemplate: false,
          isPublicTrip: false,
          permissions: getTripPermissions(trip),
          name: trip.name,
          displayContext: 'savedItemsDrawer',
        },
      }),
    )
  }, [trips])

  const showModal = useContext(ModalContext)

  const onApplySortOption = useCallback(
    (sortOption: TripItemsSortOptions) => {
      setSortOption(sortOption)
      toggleDropdown()
      localStorage.setItem(
        SAVED_ITEMS_LIST_LAST_SELECTED_SORT_OPTION_STORAGE_KEY,
        JSON.stringify(sortOption),
      )
      fireInteractionEvent(
        tripDrawerSavedItemsListSortOptionSelected(sortOption),
      )
    },
    [setSortOption, toggleDropdown],
  )

  const isMobile = useIsMobileScreen()

  const onSortChipClick = useCallback(() => {
    if (isMobile) {
      showModal(
        <SortSavedItemsModal
          onSelect={onApplySortOption}
          selectedSortOption={sortOption}
        />,
      )
    } else {
      toggleDropdown()
    }
  }, [isMobile, onApplySortOption, showModal, sortOption, toggleDropdown])

  const processedTripItems = useMemo(() => {
    let processed = [...tripItems]
    const filtersSet = new Set(filters)
    if (filters.length > 0) {
      processed = processed.filter(
        (item) =>
          filterProducts(item, filtersSet) || filterStatus(item, filtersSet),
      )
    }
    switch (sortOption) {
      case 'recently added':
        processed = sortBy(processed, (item) => item.createdAt, 'desc')
        break
      case 'alphabetical':
        processed = sortBy(processed, (item) => item.sortableName, 'asc')
        break
      case 'by trip':
        processed = sortBy(
          processed,
          (item) => tripMap[item.tripId].trip.name,
          'asc',
        )
        break
    }
    return processed
  }, [filters, sortOption, tripItems, tripMap])

  // Intialise stored sort & filter options
  useIsomorphicLayoutEffect(() => {
    const storedSortOption = localStorage.getItem(
      SAVED_ITEMS_LIST_LAST_SELECTED_SORT_OPTION_STORAGE_KEY,
    )

    if (storedSortOption) {
      const parsedOption = JSON.parse(storedSortOption)
      if (TRIP_ITEMS_SORT_OPTIONS.includes(parsedOption)) {
        setSortOption(parsedOption)
      }
    }
  }, [])

  const modalElement = useModalElementContext()

  const onTripClick = useCallback(() => {
    fireInteractionEvent(tripDrawerSavedItemsListTripClicked())
    modalElement?.resolve()
  }, [modalElement])
  const isFiltersTrackable = useRef<boolean>(false)
  useEffect(() => {
    if (filters.length > 0) {
      // After the first time a filter has been selected we can track filter changes
      // This avoids tracking 'all' on render
      isFiltersTrackable.current = true
    }
    if (isFiltersTrackable.current) {
      fireInteractionEvent(
        tripDrawerSavedItemsListFilterOptionSelected(filters),
      )
    }
  }, [filters])

  return (
    <Group direction="vertical" gap={16}>
      {processedTripItems.length > 1 && (
        <MarginContainer>
          <Group direction="horizontal" horizontalAlign="end">
            <FilterChip
              ref={sortChipRef}
              style={{ zIndex: 1 }}
              onClick={onSortChipClick}
              size="medium"
              endIcon={<LineAngleDownIcon />}
            >
              Sort: {capitalise(sortOption)}
            </FilterChip>
            <DropdownList
              size="S"
              open={isDropdownOpen}
              triggerRef={sortChipRef}
              anchorRef={sortChipRef}
              onClose={toggleDropdown}
            >
              {TRIP_ITEMS_SORT_OPTIONS.map((opt) => (
                <ListItem
                  key={opt}
                  onClick={() => onApplySortOption(opt)}
                  title={capitalise(opt)}
                  selected={sortOption === opt}
                />
              ))}
            </DropdownList>
          </Group>
        </MarginContainer>
      )}
      <ItemsContainer direction="vertical" gap={8}>
        {processedTripItems.map((item) => {
          const tripNameLabel = (
            <SavedOfferTripLabel
              trip={tripMap[item.tripId].trip}
              onClick={onTripClick}
            />
          )
          let card: JSX.Element
          if (newDetailCardImplementedTypes.has(item.type)) {
            card = (
              <DetailItemCard
                item={item}
                variant="drawer"
                tripNameLabel={tripNameLabel}
              />
            )
          } else {
            card = (
              <OldItemCardContainer>
                <ItemCard
                  item={item}
                  tripNameLabel={tripNameLabel}
                  variant="drawer"
                />
              </OldItemCardContainer>
            )
          }
          return (
            <TripContext.Provider
              key={item.id}
              value={tripMap[item.tripId].context}
            >
              {card}
            </TripContext.Provider>
          )
        })}
      </ItemsContainer>
    </Group>
  )
}

export default SavedItemsList
